import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div 
      className="min-h-screen" 
      style={{ 
        fontFamily: 'Outfit, sans-serif',
        backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
      }}
    >
      {/* Hero Section */}
      <section className="pt-28 lg:pt-32 pb-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-black mb-4"
            style={{ fontFamily: 'Rowdies, cursive' }}
          >
            Privacy Policy
          </h1>
          <div className="flex items-center justify-center gap-2 text-base">
            <Link to="/" className="text-pink-600 hover:underline">🏠 Home</Link>
            <span className="text-pink-600">»</span>
            <span className="text-pink-600">Privacy Policy</span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8 px-6 pb-24">
        <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-xl p-8 lg:p-12">
          <p className="text-gray-600 mb-8">Last Updated: December 2024</p>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Rowdies, cursive' }}>1. Introduction</h2>
            <p className="text-gray-700 mb-6">
              Welcome to 1ST Avenue ICP ("we," "our," or "us"), operated by Crazy ICP LLC. We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <a href="https://1staveicp.com" className="text-pink-600 hover:underline">1staveicp.com</a> or interact with our services.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8" style={{ fontFamily: 'Rowdies, cursive' }}>2. Information We Collect</h2>
            <p className="text-gray-700 mb-4">We may collect the following types of information:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li><strong>Personal Information:</strong> Name, email address, phone number, and any other information you voluntarily provide through our contact forms or sign-up forms.</li>
              <li><strong>Communication Preferences:</strong> Your preferences for receiving communications from us, including email and SMS/text message consent.</li>
              <li><strong>Usage Data:</strong> Information about how you use our website, including pages visited and time spent on pages.</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8" style={{ fontFamily: 'Rowdies, cursive' }}>3. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">We use your information to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Respond to your inquiries and provide customer support</li>
              <li>Send promotional messages, special offers, and updates (with your consent)</li>
              <li>Improve our website and services</li>
              <li>Comply with legal obligations</li>
            </ul>

            {/* SMS/Text Messaging Section */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6 my-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4" style={{ fontFamily: 'Rowdies, cursive' }}>📱 4. SMS/Text Messaging</h2>
              <p className="text-gray-700 mb-4">
                By opting in to SMS messages from 1ST Avenue ICP (Crazy ICP LLC), you consent to receive recurring automated promotional and informational text messages at the phone number provided.
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li><strong>Consent is not a condition of purchase.</strong></li>
                <li><strong>Message frequency varies</strong> (approximately 2-4 messages per month).</li>
                <li><strong>Message and data rates may apply.</strong></li>
              </ul>
              <p className="text-gray-700 mb-4">
                <strong>To opt out:</strong> Reply <span className="bg-red-100 text-red-700 px-2 py-1 rounded font-bold">STOP</span> to any message. You will receive a one-time confirmation of your unsubscription.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>For help:</strong> Reply <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded font-bold">HELP</span> or contact us at <a href="tel:817-829-4878" className="text-pink-600 hover:underline">817-829-4878</a>.
              </p>
              <p className="text-gray-700">
                We do not sell, rent, or share your phone number with third parties for their marketing purposes. Your information is used solely for communications from 1ST Avenue ICP.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8" style={{ fontFamily: 'Rowdies, cursive' }}>5. Information Sharing</h2>
            <p className="text-gray-700 mb-6">
              We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li><strong>Service Providers:</strong> We may share information with trusted third-party service providers who assist us in operating our website and conducting our business (e.g., email and SMS service providers).</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law or in response to valid legal processes.</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8" style={{ fontFamily: 'Rowdies, cursive' }}>6. Data Security</h2>
            <p className="text-gray-700 mb-6">
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8" style={{ fontFamily: 'Rowdies, cursive' }}>7. Your Rights</h2>
            <p className="text-gray-700 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt out of marketing communications at any time</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8" style={{ fontFamily: 'Rowdies, cursive' }}>8. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have questions about this Privacy Policy or wish to exercise your rights, please contact us:
            </p>
            <div className="bg-gray-100 rounded-xl p-6">
              <p className="text-gray-700"><strong>Crazy ICP LLC</strong> (DBA: 1ST Avenue ICP)</p>
              <p className="text-gray-700">103 NE 1st Ave, Mineral Wells, TX 76066</p>
              <p className="text-gray-700">Phone: <a href="tel:817-829-4878" className="text-pink-600 hover:underline">817-829-4878</a></p>
              <p className="text-gray-700">Email: <a href="mailto:1staveicp@gmail.com" className="text-pink-600 hover:underline">1staveicp@gmail.com</a></p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8" style={{ fontFamily: 'Rowdies, cursive' }}>9. Changes to This Policy</h2>
            <p className="text-gray-700 mb-6">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>
          </div>

          <div className="mt-12 pt-8 border-t-2 border-gray-200 text-center">
            <Link to="/sms-terms" className="text-pink-600 hover:underline font-bold mr-6">SMS Terms & Conditions</Link>
            <Link to="/contact" className="text-pink-600 hover:underline font-bold">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
